/* eslint-disable no-nested-ternary */
/* eslint camelcase: 0 */
import React from 'react';
import { Outlet } from 'react-router-dom';
import PropTypes from 'prop-types';
import { jwtDecode } from 'jwt-decode';

import { errorFallbackHandler } from '../../services/errorHandler';
import API from '../../services/apiCalls';
import { logEvents, logLevels, localStorageKeys } from '../../constants/appConstants';
import { deepFind, getParams, getPurchaseParams, getExperimentParams, getOfferValues } from '../../services/utils';
import logger from '../../services/logger';
import exchangeToken from '../../services/exchangeToken';
import routerHelper from '../../services/routerHelper';
import { isFeatureEnabled } from '../../services/externalMethods';

const Fallback = React.lazy(() => import('../../components/Fallback'));

const DataContext = React.createContext();
const config = _CONFIG_;

const CACHE_KEY_FEATURES = localStorageKeys.SPOTLIGHT_CACHE_FEATURES;
const CACHE_EXPIRATION_MS = 60 * 60 * 1000; // 1 hour

const promisifyApiGet = (url) => {
    return new Promise((resolve, reject) => {
        API.get(url, (response, error) => {
            if (error) {
                reject(error);
            } else {
                resolve(response);
            }
        });
    });
};

class DataProviderClass extends React.Component {
    constructor(props) {
        super(props);
        const { lang } = props.params;
        const { debug, access_token, theme = 'dark', experiment_groups, license_id, plans_offer, source,
            promo_product, promo_runtime, promo_devices, promo_type, promo_purchase_type, promo_code, general_offer, no_promo, bundleId, users, no_cache } = getParams(props.location.search);
        // Get cache only if the token is different from a previous use
        let cache = {
            paymentUrls: {},
            promotions: {},
        };

        if (access_token && (access_token !== sessionStorage.getItem('SPOTLIGHT_TOKEN'))) {
            // Set the token in sessionStorage to be used later in the apiCalls
            sessionStorage.setItem('SPOTLIGHT_TOKEN', access_token);
        } else {
            cache = this.getCachedState() || cache;
        }

        this.state = {
            lang,
            theme,
            debug,
            value: cache,
            promo_code,
            no_cache,
            loading: true,
        };

        const purchaseParams = getPurchaseParams(props.location.search);
        if (purchaseParams) {
            // Set the purchase params in sessionStorage to be used later in the Plans page
            sessionStorage.setItem('SPOTLIGHT_PURCHASE_PARAMS', purchaseParams);
        }

        // Save the path from store url
        this.isStore = this.props.location.pathname.indexOf('store') > -1;

        if (this.isStore || source === 'store') {
            this.state.promo_code = config.uiStorePromoCode[1]; // 'spl_upgrade_high'
        }
        this.isUiStoreSale = this.isStore || source === 'store';

        this.upgradePromotions = general_offer === 'spl_upgrade_low' || general_offer === 'spl_upgrade_high';
        if (this.upgradePromotions) {
            localStorage.setItem('SPOTLIGHT_CACHE_GENERAL_OFFER', general_offer);
        }

        this.nagPromo = this.state.promo_code === 'spl_paid_upsell';
        this.toasterPromo = this.state.promo_code === 'spl_retr_40';
        this.nagPromoHigh = this.state.promo_code === 'spl_upgrade_high';
        this.isUpgradeHigh = this.state.promo_code === 'spl_upgrade_high';
        this.isUpgradeLow = this.state.promo_code === 'spl_upgrade_low';
        this.isUpgradePromotions = this.isUpgradeHigh || this.isUpgradeLow;

        let isPromoCode;
        if (this.isUiStoreSale) {
            isPromoCode = true;
        } else if (this.toasterPromo) {
            isPromoCode = promo_code === config.retriever_config.promotionCode[0];
        } else if (this.nagPromo) {
            isPromoCode = promo_code === config.retriever_config.promotionCode[1];
        } else if (this.nagPromoHigh) {
            isPromoCode = promo_code === config.retriever_config.promotionCode[2];
        } else if (this.isUpgradeHigh) {
            isPromoCode = promo_code === config.retriever_config.promotionCode[2];
        } else if (this.isUpgradeLow) {
            isPromoCode = promo_code === config.retriever_config.promotionCode[3];
        }

        this.promoProduct = promo_product;
        this.promoDevices = promo_devices;
        this.promoRuntime = promo_runtime;
        this.tokenData = {};
        this.experiment = experiment_groups;
        this.source = source;
        this.licenseId = license_id;
        this.offer = getOfferValues(plans_offer);
        this.promotionRetriever = { promo_product, promo_runtime, promo_devices, promo_type, promo_purchase_type, promo_code,
            hasPromoCode: promo_product && promo_runtime && promo_devices && promo_type && promo_purchase_type && promo_code && isPromoCode };
        // this.noPromoRetriever = { promo_product, promo_runtime, promo_devices, promo_type, promo_purchase_type, no_promo,
        //     hasNoPromoCode: promo_product && promo_runtime && promo_devices && promo_type && promo_purchase_type && no_promo &&
        //     no_promo === 'no_promo' };
        this.noPromoRetriever = { general_offer, no_promo, hasNoPromoCode: general_offer && no_promo && no_promo === 'no_promo'};

        this.isRedirectRetriever = (this.promotionRetriever && this.promotionRetriever.hasPromoCode && license_id) || (this.noPromoRetriever && this.noPromoRetriever.hasNoPromoCode && license_id);
        this.lastExperiment = getExperimentParams(experiment_groups, 'data-provider');

        // Delete licenses cache on different license_id values
        if (this.state.value.licenses && this.licenseId) {
            const { licenses, transactions } = this.state.value;

            // Check if a transaction was recently done via one-click-payment
            let recentPurchase = false;
            if (transactions) {
                Object.keys(transactions).forEach((id) => {
                    if (transactions[id].activation_code === licenses.activationCode) {
                        recentPurchase = id;
                    }
                });
            }

            if ((Object.keys(licenses).length === 0) ||
                (Object.keys(licenses).length !== 0 && this.licenseId !== licenses.id) ||
                (Object.keys(licenses).length !== 0 && this.licenseId === licenses.id && recentPurchase)) {
                delete this.state.value.licenses;
            }
        }

        // Check if the requested page is upsell identity assistant
        this.isUpsellIdentityAssistant = (this.props.location.pathname.indexOf('identity-assistant') > -1 ||
            (this.props.location.pathname.indexOf('upsell') > -1 && this.source === 'identity_assistant'));

        // Check if the page is plans-upsell
        this.isPlansUpsell = this.props.location.pathname.indexOf('plans-upsell') > -1;

        // Check if the experiment is for paid upsell
        // this.isPaidUpsell = this.licenseId && (general_offer === 'paid-upsell' || this.isPlansUpsell);
        this.isPaidUpsell = this.licenseId && !no_promo && !this.isUpgradePromotions;

        // Save the campaign marker from toaster flow
        this.campaignMarker = getParams(props.location.search)['x-ipm_campaignmarker'];

        // Save the source from the virus alert flow
        // this.virusAlert = getParams(props.location.search).source;

        // Save the path from interstitial url
        this.isInterstitial = this.props.location.pathname.indexOf('interstitial') > -1;

        this.bundleId = bundleId ? bundleId : 'aps10';
        this.promotionBundleId = config.promotionsBundlesNew.filter(bundle => bundle === this.bundleId)[0];

        this.users = users ? users : '1';
    }

    async componentDidMount() {
        try {
            // F2P sales flows 4 fullscale (Interstitial): normalize URL from smart_scan + experiment_groups to bundleId + users + general_offer + promo_code.
            // Preserve other params (theme, startTimestamp, app_name, etc.); full reload ensures DataProvider refetches with new params.
            // Only call isFeatureEnabled for smart_scan interstitial;
            if (this.isInterstitial && this.source === 'smart_scan') {
                const isF2pSalesFlows4Fullscale = !this.licenseId && this.experiment && this.experiment.split(',').includes('f2psalesflows4.fullscale');
                const shouldNormalizeUrl = isF2pSalesFlows4Fullscale || isFeatureEnabled('PromoteProProductInSmartScanUpsell');
                if (shouldNormalizeUrl) {
                    const currentSearch = this.props.location.search || '';
                    const params = new URLSearchParams(currentSearch);
                    // Required params for this experiment
                    params.set('bundleId', 'aapd5');
                    params.set('users', '10');
                    params.set('general_offer', 'spl_upgrade_high');
                    params.set('promo_code', 'spl_upgrade_high');
                    if (isF2pSalesFlows4Fullscale) {
                        params.set('experiment_groups', 'f2psalesflows4.fullscale');
                    }
                    const newSearch = `?${params.toString()}`;
                    if (currentSearch !== newSearch) {
                        const locationPathname = this.props.location.pathname || '';
                        window.location.replace(locationPathname + newSearch);
                        return;
                    }
                }
            }

            const sessionToken = sessionStorage.getItem('SPOTLIGHT_TOKEN');

            if (sessionToken && sessionToken !== 'undefined') {
                this.tokenData = jwtDecode(sessionToken);
            }

            // if the token is one time then exchange the token
            if (this.tokenData.ot) {
                const accessToken = await exchangeToken();
                if (accessToken) {
                    sessionStorage.setItem('SPOTLIGHT_TOKEN', accessToken);
                    sessionStorage.setItem('SPOTLIGHT_EXPERIMENT_PARAMS', this.experiment);

                    const pathName = this.props.location.pathname;
                    // append identity assistant parameter after exchainging token
                    if (this.source === 'identity_assistant') {
                        window.location.href = `${pathName}?source=${this.source}`;
                    } else {
                        window.location.href = pathName;
                    }
                }
            } else {
                // if there is a no_cache parameter in the url, clear the cache
                if (this.state.no_cache) {
                    this.clearAppLicensesCache();
                }

                // Fetch all data
                await this.fetchAllData();
            }
        } catch (error) {
            logger(logLevels.ERROR, {
                source: 'DataProvider',
                action: logEvents.ERROR,
                payload: {
                    Message: 'Error in DataProvider componentDidMount',
                    Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
                },
            });
        }
    }

    componentDidUpdate() {
        logger(logLevels.DEBUG, { source: 'DataProvider', action: logEvents.STATE_CHANGED, payload: this.state });
    }

    async getAppIntegrations() {
        const bundleIds = config.upsellBundlesPrices.join(',');

        if (!bundleIds) {
            logger(logLevels.WARN, {
                source: 'DataProvider.getAppIntegrations',
                action: 'SKIPPED_API_CALL',
                payload: 'No bundle IDs found to fetch integrations, skipping API call.',
            });
            this.setState(prevState => ({
                ...prevState,
                value: {
                    ...prevState.value,
                    features: {},
                },
            }));
            return;
        }

        if (!API.endpoint.appIntegrations || typeof API.endpoint.appIntegrations !== 'string') {
            logger(logLevels.ERROR, {
                source: 'DataProvider.getAppIntegrations',
                action: 'INVALID_API_ENDPOINT_TEMPLATE',
                payload: `API.endpoint.appIntegrations is invalid or not a string: ${API.endpoint.appIntegrations}`,
            });
            this.setState(prevState => ({
                ...prevState,
                value: {
                    ...prevState.value,
                    features: {},
                },
            }));
            return;
        }

        const url = API.endpoint.appIntegrations.replace('{bundleId}', bundleIds).replace('{filterTo}', 'spotl');
        logger(logLevels.DEBUG, { source: 'DataProvider.getAppIntegrations', action: 'API_URL_GENERATED', payload: { url } });

        const cachedData = localStorage.getItem(CACHE_KEY_FEATURES);
        if (cachedData) {
            try {
                const { features, timestamp } = JSON.parse(cachedData);
                const now = new Date().getTime();

                if (now - timestamp < CACHE_EXPIRATION_MS) {
                    logger(logLevels.DEBUG, {
                        source: 'DataProvider.getAppIntegrations',
                        action: 'CACHE_HIT',
                        payload: { url, cacheAgeMs: now - timestamp, featuresCount: Object.keys(features).length },
                    });
                    this.setState(prevState => ({
                        ...prevState,
                        value: {
                            ...prevState.value,
                            features: features,
                        },
                    }));
                    return;
                }
                logger(logLevels.INFO, {
                    source: 'DataProvider.getAppIntegrations',
                    action: 'CACHE_STALE',
                    payload: { url, cacheAgeMs: now - timestamp },
                });
                localStorage.removeItem(CACHE_KEY_FEATURES);
            } catch (parseError) {
                logger(logLevels.WARN, {
                    source: 'DataProvider.getAppIntegrations',
                    action: 'CACHE_PARSE_ERROR',
                    payload: { url, error: parseError.message || String(parseError) },
                });
                localStorage.removeItem(CACHE_KEY_FEATURES);
            }
        }

        let features = {};
        try {
            logger(logLevels.DEBUG, { source: 'DataProvider.getAppIntegrations', action: 'FETCHING_API_DATA', payload: { url } });

            const apiResponse = await promisifyApiGet(url);

            logger(logLevels.DEBUG, {
                source: 'DataProvider.getAppIntegrations',
                action: 'API_RESPONSE_RECEIVED',
                payload: { url, responseDataPresent: !!apiResponse?.data },
            });

            if (apiResponse && Array.isArray(apiResponse.data)) {
                features = apiResponse.data.reduce((acc, dataItem) => {
                    const appId = dataItem?.id?.split('-')[0];
                    const customFeatures = dataItem?.attributes?.custom?.features;

                    if (appId && customFeatures) {
                        acc[appId] = customFeatures;
                    } else {
                        logger(logLevels.WARN, {
                            source: 'DataProvider.getAppIntegrations',
                            action: 'MALFORMED_API_DATA_ITEM_SKIPPED',
                            payload: { item: dataItem },
                        });
                    }
                    return acc;
                }, {});
                logger(logLevels.DEBUG, {
                    source: 'DataProvider.getAppIntegrations',
                    action: 'FEATURES_PROCESSED',
                    payload: { count: Object.keys(features).length },
                });
            } else {
                logger(logLevels.WARN, {
                    source: 'DataProvider.getAppIntegrations',
                    action: 'UNEXPECTED_API_RESPONSE_FORMAT',
                    payload: { responseType: typeof apiResponse?.data, rawResponse: apiResponse },
                });
            }

            const newState = {
                features,
                timestamp: new Date().getTime(),
            };

            // Save new data to localStorage
            localStorage.setItem(CACHE_KEY_FEATURES, JSON.stringify(newState));

            this.setState(prevState => ({
                ...prevState,
                value: {
                    ...prevState.value,
                    features: features,
                },
            }));
        } catch (error) {
            logger(logLevels.ERROR, {
                source: 'DataProvider.getAppIntegrations',
                action: logEvents.ERROR,
                payload: {
                    url,
                    errorMessage: error.message || 'Unknown network error',
                    errorDetails: (error instanceof Error) ? error.toString() : String(error),
                    errorStack: error.stack,
                },
            });

            this.setState(prevState => ({
                ...prevState,
                value: {
                    ...prevState.value,
                    features: {},
                },
            }));

            errorFallbackHandler({
                Message: `Failed to load app integrations: ${error.message || 'Network error'}`,
                Exception: (error instanceof Error) ? error.toString() : JSON.stringify(error),
            }, this.state.lang, this.props.location.pathname, this.props.location.search, this.props.navigate);
        }
    }

    async getPrices(runtime, devices, license, multipleYears) {
        const bundleIds = config.upsellBundlesPrices.join(',');
        let url = API.endpoint.appPrices.replace('{appId}', bundleIds);
        // Commented out due to the need to split the response from the backend (too much data).
        // url += '&filter[runtimes]=12,24&filter[users]=1,5,10';
        if (devices === 10) {
            url += `&filter[runtimes]=${runtime}&filter[users]=${devices}`;
        } else if (devices === 5) {
            url += `&filter[runtimes]=${runtime}&filter[users]=${devices}`;
        } else if (devices === 1) {
            url += `&filter[runtimes]=${runtime}&filter[users]=${devices}`;
        } else {
            url += '&filter[runtimes]=12,24&filter[users]=1,5,10';
        }

        if (license && !multipleYears) {
            url = API.endpoint.appPrices.replace('{appId}', license.appId);
            url += `&filter[runtimes]=${license.runtime}&filter[users]=${license.devices}`;
        } else if (license && multipleYears) {
            url = API.endpoint.appPrices.replace('{appId}', license.upsellTo);
            url += '&filter[runtimes]=24,36&filter[users]=1';
        }

        logger(logLevels.DEBUG, { source: 'DataProvider.getPrices', action: logEvents.API_CALL, payload: { url } });

        try {
            const apiResponse = await new Promise((resolve, reject) => {
                API.get(url, (response, error) => {
                    if (error) {
                        reject(error);
                    } else {
                        resolve(response);
                    }
                });
            });

            logger(logLevels.DEBUG, { source: 'DataProvider.getPrices', action: logEvents.API_CALL, payload: { url, apiResponse } });

            let prices;
            if (this.users === '10') {
                prices = {
                    yearly10Devices: {},
                    twoYears10Devices: {},
                };
            } else if (this.users === '5') {
                prices = {
                    yearly5Devices: {},
                    twoYears5Devices: {},
                };
            } else {
                prices = {
                    yearly: {},
                    yearly5Devices: {},
                    twoYears: {},
                    twoYears5Devices: {},
                    yearly10Devices: {},
                    twoYears10Devices: {},
                };
            }

            apiResponse.data.forEach((data) => {
                const { runtimes, users } = data.attributes;
                const appId = data.relationships.app.data.id;

                if (license && !multipleYears) {
                    prices.currentPlan = {};
                    prices.currentPlan[appId] = data.attributes;
                } else {
                    if (runtimes === '12' && users === '1') {
                        prices.yearly[appId] = data.attributes;
                    } else if (runtimes === '24' && users === '1') {
                        prices.twoYears[appId] = data.attributes;
                    } else if (runtimes === '12' && users === '5') {
                        prices.yearly5Devices[appId] = data.attributes;
                    } else if (runtimes === '24' && users === '5') {
                        prices.twoYears5Devices[appId] = data.attributes;
                    } else if (runtimes === '12' && users === '10') {
                        prices.yearly10Devices[appId] = data.attributes;
                    } else if (runtimes === '24' && users === '10') {
                        prices.twoYears10Devices[appId] = data.attributes;
                    } else if (runtimes === '36' && users === '1') {
                        prices.threeYears = {};
                        prices.threeYears[appId] = data.attributes;
                    } else {
                        // Fallback to yearly bucket if an unexpected runtime is returned.
                        prices.yearly[appId] = data.attributes;
                    }
                }
            });

            // If the call made for license doesn't return anything - current plan price is saved as empty
            if (license && apiResponse.data.length === 0) {
                prices.currentPlan = {
                    [license.appId]: {
                        price_string: '',
                    },
                };
            }

            let newState = {};

            const pricesCache = localStorage.getItem(localStorageKeys.SPOTLIGHT_CACHE_APP_PRICES);

            if (pricesCache) {
                const parsedPricesCache = JSON.parse(pricesCache);
                // Save the new values only if the object is not empty
                Object.keys(prices).forEach((type) => {
                    if (Object.keys(prices[type]).length !== 0) {
                        parsedPricesCache.prices[type] = prices[type];
                    }
                });
                prices = parsedPricesCache.prices;
                parsedPricesCache.timestamp = new Date().getTime();
                newState = parsedPricesCache;
            } else {
                newState = {
                    prices,
                    timestamp: new Date().getTime(),
                };
            }

            localStorage.setItem(localStorageKeys.SPOTLIGHT_CACHE_APP_PRICES, JSON.stringify(newState));
            const currentState = this.state;
            currentState.value.prices = prices;

            this.setState(currentState);
        } catch (error) {
            errorFallbackHandler({
                Message: 'Network error getPrices',
                Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
            }, this.state.lang, this.props.location.pathname, this.props.location.search, this.props.navigate);
        }
    }

    async getPaymentUrls(shopUrlType, devices, license, promoRuntime) {
        let bundleIds = config.upsellBundlesPrices.join(',');

        let subscriptionType;
        if (devices === 5) {
            subscriptionType = shopUrlType.concat('5Devices');
        } else if (devices === 10 || this.promoDevices === 10) {
            subscriptionType = shopUrlType.concat('10Devices');
        } else {
            subscriptionType = shopUrlType;
        }

        let runtime = promoRuntime ? promoRuntime : 12;
        if (shopUrlType.indexOf('yearly') > -1) {
            runtime = 12;
        } else if (shopUrlType === '2Years' || shopUrlType === 'twoYears') {
            runtime = 24;
        } else if (shopUrlType === '3Years') {
            runtime = 36;
        }
        let upsellTo = '';

        // Change values if license is available
        if (license && license.additionalCalls) {
            bundleIds = license.upsellTo;
            subscriptionType = 'promotions';

            if (shopUrlType === '2Years' || shopUrlType === '3Years') {
                subscriptionType += `${shopUrlType}`;
            }
        } else if (license && license.switchToYearly) {
            bundleIds = license.appId;
            subscriptionType = 'switchToYearly';
            runtime = 12;
        } else if (license && license.renewLicense) {
            bundleIds = license.appId;
            subscriptionType = (shopUrlType === 'yearly-upsellTo') ? 'upsellLicense' : 'renewLicense';
        } else if (license && license.retrieverRedirect) {
            bundleIds = license.upsellTo;
            if (promoRuntime === 12 && this.isUpgradePromotions) {
                subscriptionType = 'retrieverYearly';
            } else if (promoRuntime === 24 && this.isUpgradePromotions) {
                subscriptionType = 'retriever2Years';
            } else {
                subscriptionType = 'retriever';
            }
        } else if (license && license.paidUpsell) {
            bundleIds = license.upsellTo.primaryPromotion || license.upsellTo.primeMerge || license.appId;
            upsellTo = license.upsellTo.primaryPromotion || license.upsellTo.primeMerge;
            subscriptionType = 'paidUpsell';

            if (shopUrlType === 'yearly-secondaryPromotion') {
                bundleIds = upsellTo = license.upsellTo.secondaryPromotion;
                subscriptionType = 'paidUpsellSecondary';
            } else if (shopUrlType === 'yearly-onlyMerge') {
                bundleIds = license.appId;
                upsellTo = license.upsellTo.onlyMerge;
                subscriptionType = 'paidUpsellSecondary';
            }
        }

        let paymentUrl =
            API.endpoint.paymentUrls
                .replace('{appId}', bundleIds)
                .replace('{source}', 'spotlight')
                .replace('{lang}', this.state.lang)
                .replace('{runtime}', promoRuntime ? promoRuntime : runtime ? runtime : 12)
                .replace('{devices}', this.promoDevices ? this.promoDevices : devices ? devices : 1);
        // Add additional params values if license is available
        if (license && (license.additionalCalls || license.retrieverRedirect)) {
            paymentUrl += `&filter[subsource]=${license.subsource}&filter[license]=${this.licenseId}&upsellTo=${license.upsellTo}`;
        } else if (license && license.switchToYearly) {
            paymentUrl += `&filter[license]=${this.licenseId}&upsellTo=${license.appId}`;
        } else if (license && license.renewLicense) {
            paymentUrl += (shopUrlType === 'yearly-upsellTo') ?
                `&filter[license]=${this.licenseId}&upsellTo=${license.upsellTo}` : `&filter[license]=${this.licenseId}`;
        } else if (license && license.paidUpsell) {
            paymentUrl += ((license.upsellTo.primaryPromotion || license.upsellTo.primeMerge) && shopUrlType !== 'yearly-onlyMerge') ?
                `&filter[subsource]=${license.subsource}&filter[license]=${this.licenseId}&upsellTo=${upsellTo}&filter[users]=1` :
                `&filter[license]=${this.licenseId}&upsellTo=${upsellTo}&filter[users]=1`; // force 1 device
            // ! Even if the name is Avira Prime (5 or 25 devices) the configuration in product catalog is users/devices=1 and this is how you should request it from the backend.
        }

        logger(logLevels.DEBUG, { source: 'DataProvider.getPaymentUrls', action: logEvents.API_CALL, payload: { paymentUrl } });

        try {
            const apiResponse = await new Promise((resolve, reject) => {
                API.get(paymentUrl, (response, error) => {
                    if (error) {
                        reject(error);
                    } else {
                        resolve(response);
                    }
                });
            });

            logger(logLevels.DEBUG, { source: 'DataProvider.getPaymentUrls', action: logEvents.API_CALL, payload: { paymentUrl, apiResponse } });

            const paymentUrls = {
                [subscriptionType]: {},
            };
            apiResponse.data.forEach((data) => {
                const id = data.id.split('-')[1];
                const url = data.attributes.url;
                paymentUrls[subscriptionType][id] = url;
            });

            let newState = {};

            const paymentUrlCache = localStorage.getItem(localStorageKeys.SPOTLIGHT_CACHE_PAYMENT_URLS);
            if (paymentUrlCache) {
                const parsedPaymentUrlCache = JSON.parse(paymentUrlCache);
                parsedPaymentUrlCache.timestamp = new Date().getTime();
                parsedPaymentUrlCache.paymentUrls[subscriptionType] = paymentUrls[subscriptionType];
                newState = parsedPaymentUrlCache;
            } else {
                newState = {
                    paymentUrls,
                    timestamp: new Date().getTime(),
                };
            }

            localStorage.setItem(localStorageKeys.SPOTLIGHT_CACHE_PAYMENT_URLS, JSON.stringify(newState));
            const currentState = this.state;
            currentState.value.paymentUrls[subscriptionType] = paymentUrls[subscriptionType];

            this.setState(currentState);
        } catch (error) {
            errorFallbackHandler({
                Message: 'Network error getPaymentUrls',
                Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
            }, this.state.lang, this.props.location.pathname, this.props.location.search, this.props.navigate);
        }
    }

    async getDeviceData() {
        const url = API.endpoint.myDevice;
        logger(logLevels.DEBUG, { source: 'DataProvider.getDeviceData', action: logEvents.API_CALL, payload: { url } });

        try {
            const apiResponse = await new Promise((resolve, reject) => {
                API.get(url, (response, error) => {
                    if (error) {
                        reject(error);
                    } else {
                        resolve(response);
                    }
                });
            });

            logger(logLevels.DEBUG, { source: 'DataProvider.getDeviceData', action: logEvents.API_CALL, payload: { url, apiResponse } });

            const device = deepFind(apiResponse, 'data') || {};
            let newState = {};

            const deviceCache = localStorage.getItem(localStorageKeys.SPOTLIGHT_CACHE_DEVICE_DATA);

            if (deviceCache) {
                const parsedDeviceCache = JSON.parse(deviceCache);

                // Check if the device-id from cache is different than the current device-id
                if (parsedDeviceCache.device && parsedDeviceCache.device.id &&
                    (parsedDeviceCache.device.id !== device.id)) {
                    // Repeat the calls to get the paymentUrls for the new device
                    await Promise.all([
                        this.getPaymentUrls('twoYears'),
                        this.getPaymentUrls('twoYears', 5),
                        this.getPaymentUrls('twoYears', 10),
                        this.getPaymentUrls('yearly'),
                        this.getPaymentUrls('yearly', 5),
                        this.getPaymentUrls('yearly', 10),
                    ]);
                }

                parsedDeviceCache.timestamp = new Date().getTime();
                parsedDeviceCache.device = device;
                newState = parsedDeviceCache;
            } else {
                newState = {
                    device,
                    timestamp: new Date().getTime(),
                };
            }
            localStorage.setItem(localStorageKeys.SPOTLIGHT_CACHE_DEVICE_DATA, JSON.stringify(newState));
            const currentState = this.state;
            currentState.value.device = device;
            this.setState(currentState);
        } catch (error) {
            // Handle errors
            logger(logLevels.ERROR, { source: 'DataProvider.getDeviceData', action: logEvents.ERROR, payload: { url, error } });
        }
    }

    async getPromotions(shopUrlType, users, appId) {
        let type = 'public';
        let runtime = 12;
        const product = this.promoProduct ? this.promoProduct : appId ? appId : this.promotionBundleId;
        let devices = this.promoDevices ? this.promoDevices : users ? users : 1;

        // Set type based on shopUrlType value
        if (shopUrlType === 'yearly' || shopUrlType === 'twoYears' || shopUrlType === 'threeYears') {
            type = 'public';
        } else if (shopUrlType === 'trial') {
            type = 'subscription_trial';
        }

        // Set runtime based on shopUrlType value
        if (shopUrlType === 'yearly' || shopUrlType === 'trial') {
            runtime = 12;
        } else if (shopUrlType === 'twoYears') {
            runtime = 24;
        } else if (shopUrlType === 'threeYears') {
            runtime = 36;
        }

        // Set promotionType based on shopUrlType and users values
        let promotionType = shopUrlType;
        if (users === 5) {
            promotionType = promotionType.concat('5Devices');
        } else if (users === 10) {
            promotionType = promotionType.concat('10Devices');
        }

        // Set devices based on custom value
        if (appId === 'isec0' && (this.offer.isValid && this.offer.isCascadingDiscount)) {
            devices = 5;
        }

        if (this.isPaidUpsell && !this.isUiStoreSale) {
            devices = 1;
        }

        const promotionsUrl = API.endpoint.promotions
            .replace('{type}', type)
            .replace('{source}', 'spotlight')
            .replace('{purchaseType}', 'new')
            .replace('{product}', product)
            .replace('{runtime}', runtime)
            .replace('{devices}', devices);

        logger(logLevels.DEBUG, { source: 'DataProvider.getPromotions', action: logEvents.API_CALL, payload: { promotionsUrl } });

        try {
            const apiResponse = await new Promise((resolve, reject) => {
                API.get(promotionsUrl, (response, error) => {
                    if (error) {
                        reject(error);
                    } else {
                        resolve(response);
                    }
                });
            });

            logger(logLevels.DEBUG, { source: 'DataProvider.getPromotions', action: logEvents.API_CALL, payload: { promotionsUrl, apiResponse } });

            let promotions = {
                [promotionType]: {},
            };

            if (deepFind(apiResponse, 'data')) {
                apiResponse.data.forEach((data) => {
                    const { promotion_sub_source, product_acronym, code } = data.attributes;
                    const subsource = promotion_sub_source ? promotion_sub_source : code;
                    promotions[promotionType][subsource] = {};
                    promotions[promotionType][subsource][product_acronym] = data.attributes;
                });
            }

            let newState = {};

            const promotionsCache = localStorage.getItem(localStorageKeys.SPOTLIGHT_CACHE_PROMOTIONS);
            if (promotionsCache) {
                const parsedPromotionsCache = JSON.parse(promotionsCache);

                if (!parsedPromotionsCache.promotions[promotionType]) {
                    parsedPromotionsCache.promotions[promotionType] = promotions[promotionType];
                } else {
                    Object.keys(promotions[promotionType]).forEach((subsource) => {
                        if (!parsedPromotionsCache.promotions[promotionType][subsource]) {
                            parsedPromotionsCache.promotions[promotionType][subsource] = promotions[promotionType][subsource];
                        } else {
                            if (!parsedPromotionsCache.promotions[promotionType][subsource][product]) {
                                parsedPromotionsCache.promotions[promotionType][subsource][product] = promotions[promotionType][subsource][product];
                            }
                        }
                    });
                }

                promotions = parsedPromotionsCache.promotions;
                parsedPromotionsCache.timestamp = new Date().getTime();
                newState = parsedPromotionsCache;
            } else {
                newState = {
                    promotions,
                    timestamp: new Date().getTime(),
                };
            }

            localStorage.setItem(localStorageKeys.SPOTLIGHT_CACHE_PROMOTIONS, JSON.stringify(newState));

            const currentState = this.state;
            currentState.value.promotions[promotionType] = promotions[promotionType];
            this.setState(currentState);
        } catch (error) {
            errorFallbackHandler({
                Message: 'Network error getPromotions',
                Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
            }, this.state.lang, this.props.location.pathname, this.props.location.search, this.props.navigate);
        }
    }

    async getLicenses(licenseId) {
        const url = API.endpoint.licenses.replace('{id}', licenseId);
        logger(logLevels.DEBUG, { source: 'DataProvider.getLicenses', action: logEvents.API_CALL, payload: { url } });

        try {
            const apiResponse = await new Promise((resolve, reject) => {
                API.get(url, (response, error) => {
                    if (error) {
                        reject(error);
                    } else {
                        resolve(response);
                    }
                });
            });

            logger(logLevels.DEBUG, { source: 'DataProvider.getLicenses', action: logEvents.API_CALL, payload: { url, apiResponse } });

            // Save all licenses or empty object if error
            let licenses = deepFind(apiResponse, 'data') || {};
            // Check that only one license is received
            const licenseAttributes = deepFind(apiResponse, 'data.attributes');

            if (licenseAttributes) {
                const license = {
                    subscriptionType: licenseAttributes.subscription_type,
                    appId: apiResponse.data.relationships.app.data.id,
                    runtime: licenseAttributes.runtime,
                    type: licenseAttributes.type,
                    cmp: licenseAttributes.cmp,
                    devices: licenseAttributes.devices_limit,
                    subscription: licenseAttributes.subscription,
                    modifyDate: licenseAttributes.modify_date,
                    activationCode: apiResponse.data.relationships['activation-code'].data.id,
                    id: apiResponse.data.id,
                    expired: licenseAttributes.expired,
                };

                // Set default values for trial
                if (license.devices === null) {
                    license.devices = 1;
                }

                if (license.runtime === '30') {
                    license.runtime = 1;
                }

                // Logic to determine the subscription type (only monthly or yearly values)
                if (license.subscriptionType !== 'prepaid') {
                    if (license.runtime === 12 || license.runtime === 1) {
                        if (license.subscriptionType === 'yearly' ||
                            license.subscriptionType === 'annual' ||
                            license.subscriptionType === 'annual-trial') {
                            license.subscriptionType = 'yearly';
                        } else if (license.subscriptionType === 'monthly' ||
                            license.subscriptionType === 'monthly-trial') {
                            license.subscriptionType = 'monthly';
                        }
                    } else if (license.runtime !== 1 && license.runtime !== 12) {
                        if (license.runtime % 12 !== 0) {
                            license.subscriptionType = 'monthly';
                        } else if (license.runtime % 12 === 0) {
                            license.subscriptionType = 'yearly';
                        }
                    }
                } else if (license.subscriptionType === 'prepaid') {
                    if (license.runtime === 1) {
                        license.subscriptionType = 'monthly';
                    } else if (license.runtime === 12) {
                        license.subscriptionType = 'yearly';
                    }
                } else if (license.type === 'eval' && !license.cmp) {
                    license.subscriptionType = 'monthly';
                } else if (license.type === 'eval' && license.cmp === 'cobi2020avpro') {
                    license.subscriptionType = 'yearly';
                }

                // Check if the appId is configured to save the values from the config
                if (!this.offer.isValid && !this.isRedirectRetriever && !this.isPaidUpsell && !this.isUpgradePromotions) {
                    config.upsell_apps_config.forEach((promotionType) => {
                        if (promotionType.appIds.indexOf(license.appId) > -1) {
                            license.product = promotionType.productType;
                            license.promotion = promotionType.promotion;
                            license.upsellTo = promotionType.upsellTo;
                            license.subsource = promotionType.subsource;
                        }
                    });
                }

                // add condition for this.isUpgradePromotions to upgrade to Prime
                if ( this.isUpgradePromotions && this.bundleId === 'aps10' && !this.isRedirectRetriever) {
                    license.upsellTo = config.upsellMainBundle;
                    license.subsource = this.isUpgradeHigh
                        ? config.retriever_config.promotionSubsource[2] // 'upgrade_high'
                        : config.retriever_config.promotionSubsource[3]; // 'upgrade_low'
                    license.retrieverRedirect = true;
                }

                // Check if license is eligibile for upsell to yearly
                if ((config.upsellYearlyBundles.indexOf(license.appId) > -1) && license.subscriptionType === 'monthly' && !this.offer.isValid && !this.isRedirectRetriever) {
                    license.switchToYearly = true;
                }

                // OE-7333 Deactivated isTrialConversion additional calls till further notice
                // // Check if license is eligibile for trial conversion
                // if (license.appId === config.trialPageBundle && licenseAttributes.subscription_type === 'annual-trial') {
                //     license.isTrialConversion = true;
                //     // Get the App Recommendations
                //     this.getAppRecommendations();
                //     // Get the Payment Types
                //     this.getPaymentTypes();
                // }

                // Check if license is eligibile for renewal or upsell
                if (this.offer.isValid && this.offer.isRenewal) {
                    license.renewLicense = true;

                    if (config.upsellYearlyBundles.indexOf(license.appId) === -1) {
                        license.upsellTo = config.upsellMainBundle;
                        license.customLicense = true;
                    } else if (license.appId === config.upsellMainBundle) {
                        license.upsellTo = license.subscriptionType === 'monthly' ? config.upsellMainBundle : config.upsellYearlyBundles[1];
                    } else if (license.appId === config.upsellYearlyBundles[1] && license.subscriptionType === 'monthly') {
                        license.upsellTo = config.upsellYearlyBundles[1];
                    }
                }

                // Check if license is eligibile for upgrade
                if (config.plansPageBundles.indexOf(license.appId) === -1 && !license.promotion) {
                    license.isLicenseUpgrade = true;
                    // Get the License Integrations
                    this.getLicenseIntegrations('upgrade', license.appId);
                }

                // Check if license is eligibile for retriever
                if (this.isRedirectRetriever && !license.promotion) {
                    // Set the retriver case to apply the merge license promotion
                    license.retrieverRedirect = true;
                    if (this.promotionRetriever.hasPromoCode) {
                        if (this.toasterPromo) {
                            license.subsource = this.toasterPromo
                                ? config.retriever_config.promotionSubsource[0]
                                : config.retriever_config.promotionSubsource[1]; // 'renew', 'paid_upsell'
                        } else if (this.nagPromoHigh) {
                            license.subsource = config.retriever_config.promotionSubsource[2]; // 'upgrade_high'
                        } else if (this.promotionRetriever.hasPromoCode && this.isUpgradePromotions) {
                            license.subsource = this.isUpgradeHigh
                                ? config.retriever_config.promotionSubsource[2] // 'upgrade_high'
                                : config.retriever_config.promotionSubsource[3]; // 'upgrade_low'
                        } else {
                            license.subsource = config.retriever_config.promotionSubsource[1]; // 'paid_upsell'
                        }
                    }
                    // ! condition based on license Id from promo url
                    if (this.nagPromo || this.isUpgradePromotions || this.nagPromoHigh) {
                        license.upsellTo = this.promoProduct;
                    } else {
                        license.upsellTo = license.appId;
                    }

                    if (config.retriever_config.promotionAppIds.indexOf(license.appId) === -1) {
                        // Update the upsellTo with the main appId value
                        config.retriever_config.config.forEach((appFamily) => {
                            if (appFamily.appIds.indexOf(license.appId) > -1) {
                                license.upsellTo = appFamily.mainAppId;
                                license.hasMainAppId = true;
                            }
                        });

                        if (!license.hasMainAppId) {
                            // Update the retriver case to apply the default promotion
                            license.retrieverRedirect = false;
                            license.retrieverDefaultFlow = true;
                        }
                    }

                    // Update the retriver case to apply the default promotion on expired license
                    if (license.expired) {
                        license.retrieverRedirect = false;
                        license.retrieverDefaultFlow = true;
                    }
                }

                // Check if the license appId is eligible for upsell on paid upsell experiment
                if (this.isPaidUpsell && !this.isRedirectRetriever) {
                    const { promotions, campaigns } = config.paid_upsell_config;
                    const { appIds, upsellTo, promotionCode, subsource } = promotions;

                    if (appIds.primaryPromotion.indexOf(license.appId) > -1) {
                        license.upsellTo = {};
                        license.upsellTo.primaryPromotion = upsellTo.primaryPromotion;
                        license.promotionCode = promotionCode;
                        license.subsource = subsource;
                        license.paidUpsell = true;

                        // Check if additional calls should be made
                        campaigns.data_provider.forEach((campaign) => {
                            if (campaign.campaignMarkers.indexOf(this.campaignMarker) > -1) {
                                if (appIds[campaign.category].indexOf(license.appId) > -1) {
                                    license.upsellTo[campaign.category] = upsellTo[campaign.category];
                                }
                            }
                        });
                    } else if (appIds.primeMerge.indexOf(license.appId) > -1) {
                        license.upsellTo = {};
                        license.upsellTo.primeMerge = upsellTo.primeMerge;
                        license.paidUpsell = true;
                        license.promotionCode = promotionCode;
                        license.subsource = subsource;
                    }
                }

                // Save only the calculated values
                licenses = license;
                // Check if the license call is for the onboarding flow
            } else if (licenseId === 'onboarding') {
                // Save only one license if available (the most important one)
                const licenseOnboarding = {
                    appIds: [],
                };

                // Check if the licenses are available
                if (Object.keys(licenses).length > 0) {
                    licenses.forEach((license) => {
                        const appId = license.relationships.app.data.id;
                        if (!license.attributes.expired && licenseOnboarding.appIds.indexOf(appId) === -1) {
                            licenseOnboarding.appIds.push(appId);
                        }
                    });

                    // Check if the appId is configured to save the values from the config
                    if (licenseOnboarding.appIds.length > 0) {
                        licenseOnboarding.appIds.forEach((appId) => {
                            config.onboarding_offer_config.license_config.forEach((licenseGroup) => {
                                if ((licenseGroup.appIds.indexOf(appId) > -1 && !licenseOnboarding.priority) ||
                                    (licenseGroup.appIds.indexOf(appId) > -1 && licenseOnboarding.priority && licenseOnboarding.priority > licenseGroup.priority)) {
                                    licenseOnboarding.productType = licenseGroup.productType;
                                    licenseOnboarding.mainAppId = licenseGroup.mainAppId;
                                    licenseOnboarding.boxshotAvailable = licenseGroup.boxshotAvailable;
                                    licenseOnboarding.priority = licenseGroup.priority;
                                }
                            });
                        });
                    }
                }

                // Check if the user is free or make additional call
                if (Object.keys(licenses).length === 0 || licenseOnboarding.appIds.length === 0) {
                    licenseOnboarding.isFreeLicense = true;
                } else {
                    licenseOnboarding.isLicenseOnboarding = true;
                    // Get the License Integrations
                    const licenseAppId = licenseOnboarding.mainAppId || licenseOnboarding.appIds[0];
                    this.getLicenseIntegrations('onboarding', licenseAppId);
                }

                // Save only the calculated values
                licenses = licenseOnboarding;
                // Check if the license call is for the retriver redirect
            } else if (this.isRedirectRetriever) {
                // Update the retriver case to apply the default promotion
                licenses.retrieverRedirect = false;
                licenses.retrieverDefaultFlow = true;
            }

            const newState = {
                licenses,
                timestamp: new Date().getTime(),
            };
            localStorage.setItem(localStorageKeys.SPOTLIGHT_CACHE_LICENSES, JSON.stringify(newState));
            const currentState = this.state;
            currentState.value.licenses = licenses;

            if (licenses.promotion && !this.isUpgradePromotions) {
                // calls to get the paymentUrls & current plan price
                licenses.additionalCalls = true;
                this.getPaymentUrls(licenses.subscriptionType, 1, licenses);
                this.getPrices(licenses.runtime, licenses.devices, licenses);

                if (licenses.runtime === 24 || licenses.runtime === 36) {
                    this.getPaymentUrls('2Years', 1, licenses);
                    this.getPaymentUrls('3Years', 1, licenses);
                    this.getPrices(licenses.runtime, licenses.devices, licenses, 'multipleYears');
                    this.getPromotions('twoYears');
                    this.getPromotions('threeYears');
                }
            } else if (licenses.switchToYearly) {
                // call to get the paymentUrls
                this.getPaymentUrls(licenses.subscriptionType, 1, licenses);
            } else if (licenses.renewLicense) {
                // calls to get the paymentUrls
                this.getPaymentUrls(licenses.subscriptionType, licenses.devices, licenses);
                if (licenses.upsellTo) {
                    this.getPaymentUrls('yearly-upsellTo', licenses.devices, licenses);
                }

                // call to get the current plan price
                if (licenses.customLicense) {
                    this.getPrices(licenses.runtime, licenses.devices, licenses);
                }
            } else if (licenses.retrieverRedirect) {
                licenses.subscriptionType = licenses.subscriptionType === 'monthly' ? 'yearly' : licenses.subscriptionType;
                licenses.devices = licenses.upsellTo === 'aps10' || licenses.upsellTo === 'aps20' ? 1 : licenses.devices;
                // calls to get the paymentUrl
                if (this.isUpgradePromotions) {
                    this.getPaymentUrls(licenses.subscriptionType, this.promoDevices ? this.promoDevices : 1, licenses);
                    this.getPaymentUrls(licenses.subscriptionType.concat('Yearly'), this.promoDevices ? this.promoDevices : 1, licenses, 12);
                    this.getPaymentUrls(licenses.subscriptionType.concat('2Years'), this.promoDevices ? this.promoDevices : 1, licenses, 24);
                } else {
                    this.getPaymentUrls(licenses.subscriptionType, licenses.devices, licenses, this.promoRuntime);
                }
            } else if (licenses.paidUpsell) {
                // calls to get the paymentUrls & current plan price
                this.getPaymentUrls(licenses.subscriptionType, 1, licenses); // for payment url for paid upsell we need to call with 1 device
                this.getPrices(licenses.runtime, licenses.devices, licenses);

                if (licenses.upsellTo.secondaryPromotion) {
                    this.getPaymentUrls('yearly-secondaryPromotion', licenses.devices, licenses);
                    this.getPromotions('yearly', 1, licenses.upsellTo.secondaryPromotion);
                } else if (licenses.upsellTo.onlyMerge) {
                    this.getPaymentUrls('yearly-onlyMerge', licenses.devices, licenses);
                }

                if (this.isPaidUpsell) {
                    // if license ID is aps10 call promotions for aps20
                    if (licenses.appId === 'aps10') {
                        this.getPromotions('yearly', 1, 'aps20');
                    }
                    // calls to get the paymentUrls & current plan price
                    this.getPaymentUrls(licenses.subscriptionType, 1, licenses);
                    this.getPrices(licenses.runtime, licenses.devices, licenses);

                    if (licenses.upsellTo.secondaryPromotion) {
                        this.getPaymentUrls('yearly-secondaryPromotion', 1, licenses);
                        this.getPromotions('yearly', 1, licenses.upsellTo.secondaryPromotion);
                    } else if (licenses.upsellTo.onlyMerge) {
                        this.getPaymentUrls('yearly-onlyMerge', licenses.devices, licenses);
                    }
                }
            }

            this.setState(currentState);
        } catch (error) {
            errorFallbackHandler({
                Message: 'Network error getLicenses',
                Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
            }, this.state.lang, this.props.location.pathname, this.props.location.search, this.props.navigate);
        }
    }

    // don't use this function anymore
    getAppRecommendations() {
        const url = API.endpoint.appRecommendations;
        logger(logLevels.DEBUG, { source: 'DataProvider.getAppRecommendations', action: logEvents.API_CALL, payload: { url } });

        API.get(url, (response, error) => {
            logger(logLevels.DEBUG, { source: 'DataProvider.getAppRecommendations', action: logEvents.API_CALL, payload: { url, response, error } });

            if (error) {
                return errorFallbackHandler({
                    Message: 'Network error getAppRecommendations',
                    Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
                }, this.state.lang, this.props.location.pathname, this.props.location.search, this.props.navigate);
            }

            const recommendations = {};
            response.data.forEach((data) => {
                recommendations[data.attributes.acronym] = data.attributes;
                recommendations[data.attributes.acronym].id = data.id;
            });

            const newState = {
                recommendations,
                timestamp: new Date().getTime(),
            };
            localStorage.setItem(localStorageKeys.SPOTLIGHT_CACHE_APP_RECOMMENDATIONS, JSON.stringify(newState));
            const currentState = this.state;
            currentState.value.recommendations = recommendations;

            return this.setState(currentState);
        });
    }

    // don't use this function anymore
    getPaymentTypes() {
        const url = API.endpoint.paymentTypes;
        logger(logLevels.DEBUG, { source: 'DataProvider.getPaymentTypes', action: logEvents.API_CALL, payload: { url } });

        API.get(url, (response, error) => {
            logger(logLevels.DEBUG, { source: 'DataProvider.getPaymentTypes', action: logEvents.API_CALL, payload: { url, response, error } });

            if (error) {
                return errorFallbackHandler({
                    Message: 'Network error getPaymentTypes',
                    Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
                }, this.state.lang, this.props.location.pathname, this.props.location.search, this.props.navigate);
            }

            const paymentTypes = {};
            response.data.forEach((data, index) => {
                paymentTypes[index] = data.attributes;
                paymentTypes[index].id = data.id;
            });

            const newState = {
                paymentTypes,
                timestamp: new Date().getTime(),
            };
            localStorage.setItem(localStorageKeys.SPOTLIGHT_CACHE_PAYMENT_TYPES, JSON.stringify(newState));
            const currentState = this.state;
            currentState.value.paymentTypes = paymentTypes;

            return this.setState(currentState);
        });
    }

    async getLicenseIntegrations(licenseFlow, licenseAppId, bundlesComponents) {
        let bundleIds = [...config.plansPageBundles, licenseAppId];

        if (licenseFlow === 'upgrade' && bundlesComponents) {
            bundleIds = [...bundlesComponents, licenseAppId];
        } else if (licenseFlow === 'onboarding') {
            bundleIds = licenseAppId;
        }

        const url = API.endpoint.appIntegrations.replace('{bundleId}', bundleIds).replace('{filterTo}', 'dashboard');

        logger(logLevels.DEBUG, { source: 'DataProvider.getAppIntegrations', action: logEvents.API_CALL, payload: { url } });

        try {
            const apiResponse = await new Promise((resolve, reject) => {
                API.get(url, (response, error) => {
                    if (error) {
                        reject(error);
                    } else {
                        resolve(response);
                    }
                });
            });

            logger(logLevels.DEBUG, { source: 'DataProvider.getAppIntegrations', action: logEvents.API_CALL, payload: { url, apiResponse} });

            let licenseIntegrations = {};

            // Save values for the initial call on the upgrade flow
            if (licenseFlow === 'upgrade' && !bundlesComponents) {
                licenseIntegrations.plansBundles = {};

                // Save the app components of the main bundles
                apiResponse.included.forEach((data) => {
                    if (data.id !== licenseAppId) {
                        licenseIntegrations.plansBundles[data.id] = [];

                        data.relationships.apps.data.forEach((app) => {
                            licenseIntegrations.plansBundles[data.id].push(app.id);
                        });
                    }
                });

                // Save the name of the current license
                apiResponse.data.forEach((data) => {
                    const appId = data.id.split('-')[0];

                    if (appId === licenseAppId) {
                        licenseIntegrations.licenseName = data.attributes.custom.manifest.name;
                    }
                });

                if (licenseIntegrations.plansBundles[config.plansPageBundles[0]] &&
                    licenseIntegrations.plansBundles[config.plansPageBundles[1]] &&
                    licenseIntegrations.plansBundles[config.plansPageBundles[2]]
                ) {
                    // Save the common app ids for a first check
                    const plansBundlesApps = {
                        0: licenseIntegrations.plansBundles[config.plansPageBundles[0]],
                        1: licenseIntegrations.plansBundles[config.plansPageBundles[1]],
                        2: licenseIntegrations.plansBundles[config.plansPageBundles[2]],
                    };

                    const commonAppIds = Object
                        .keys(plansBundlesApps)
                        .map(elem => plansBundlesApps[elem])
                        .reduce((a, b) => b.filter(Set.prototype.has, new Set(a)));

                    // Check if the appId of the license is amongst common app ids
                    licenseIntegrations.isDirectlyUpgradable = commonAppIds.indexOf(licenseAppId) > -1;

                    // Save unique app ids for a further app-integrations call
                    if (!licenseIntegrations.isDirectlyUpgradable) {
                        const uniqueAppIds = [...new Set([
                            ...licenseIntegrations.plansBundles[config.plansPageBundles[0]],
                            ...licenseIntegrations.plansBundles[config.plansPageBundles[1]],
                            ...licenseIntegrations.plansBundles[config.plansPageBundles[2]],
                        ])];

                        // Make a followup call with the unique app ids
                        if (uniqueAppIds) {
                            licenseIntegrations.additionalCall = true;
                            this.getLicenseIntegrations('upgrade', licenseAppId, uniqueAppIds);
                        }
                    }
                }

                // Save values for the followup call on the upgrade flow
            } else if (licenseFlow === 'upgrade' && bundlesComponents) {
                // Save the license licenseAppId
                licenseIntegrations.licenseAppId = licenseAppId;

                // Save the name & prototype of the bundles components
                licenseIntegrations.license = {};
                licenseIntegrations.bundlesComponents = {};

                apiResponse.data.forEach((data) => {
                    const appId = data.id.split('-')[0];
                    const type = appId === licenseAppId ? 'license' : 'bundlesComponents';

                    licenseIntegrations[type][appId] = {};
                    licenseIntegrations[type][appId].name = data.attributes.custom.manifest.name;
                });

                apiResponse.included.forEach((data) => {
                    const appId = data.id;
                    const type = appId === licenseAppId ? 'license' : 'bundlesComponents';

                    if (!licenseIntegrations[type][appId]) {
                        licenseIntegrations[type][appId] = {};
                    }

                    licenseIntegrations[type][appId].oeName = data.attributes.resource.oeName;
                    licenseIntegrations[type][appId].prototype = data.attributes.prototype;
                });

                // Save values for the onboarding flow
            } else if (licenseFlow === 'onboarding') {
                licenseIntegrations[licenseFlow] = {};

                apiResponse.data.forEach((data) => {
                    licenseIntegrations[licenseFlow].appId = data.id.split('-')[0];
                    licenseIntegrations[licenseFlow].productName = data.attributes.custom.manifest.name;
                });

                apiResponse.included.forEach((data) => {
                    licenseIntegrations[licenseFlow].productBoxshot = data.attributes.resource.cwStoreBoxshot || data.attributes.resource.thumbailPicture;
                });
            }

            let newState = {};

            const licenseIntegrationsCache = localStorage.getItem(localStorageKeys.SPOTLIGHT_CACHE_LICENSE_INTEGRATIONS);
            if (licenseIntegrationsCache) {
                const parsedLicenseIntegrationsCache = JSON.parse(licenseIntegrationsCache);

                Object.keys(licenseIntegrations).forEach((property) => {
                    // Save the new values
                    if (!parsedLicenseIntegrationsCache.licenseIntegrations[property]) {
                        parsedLicenseIntegrationsCache.licenseIntegrations[property] = licenseIntegrations[property];
                    }
                });

                licenseIntegrations = parsedLicenseIntegrationsCache.licenseIntegrations;
                parsedLicenseIntegrationsCache.timestamp = new Date().getTime();
                newState = parsedLicenseIntegrationsCache;
            } else {
                newState = {
                    licenseIntegrations,
                    timestamp: new Date().getTime(),
                };
            }

            localStorage.setItem(localStorageKeys.SPOTLIGHT_CACHE_LICENSE_INTEGRATIONS, JSON.stringify(newState));
            const currentState = this.state;
            currentState.value.licenseIntegrations = licenseIntegrations;

            this.setState(currentState);
        } catch (error) {
            errorFallbackHandler({
                Message: 'Network error getLicenseIntegrations',
                Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
            }, this.state.lang, this.props.location.pathname, this.props.location.search, this.props.navigate);
        }
    }

    async getPing() {
        const url = API.endpoint.ping;
        logger(logLevels.DEBUG, { source: 'DataProvider.getPing', action: logEvents.API_CALL, payload: { url } });

        try {
            const apiResponse = await new Promise((resolve, reject) => {
                API.ping(url, (response, error) => {
                    if (error) {
                        reject(error);
                    } else {
                        resolve(response);
                    }
                });
            });

            logger(logLevels.DEBUG, { source: 'DataProvider.getPing', action: logEvents.API_CALL, payload: { url, apiResponse } });

            const ping = {
                country: apiResponse['x-avira-country-code'],
            };

            const newState = {
                ping,
                timestamp: new Date().getTime(),
            };
            localStorage.setItem(localStorageKeys.SPOTLIGHT_CACHE_PING, JSON.stringify(newState));
            const currentState = this.state;
            currentState.value.ping = ping;

            this.setState(currentState);
        } catch (error) {
            logger(logLevels.DEBUG, { source: 'DataProvider.getPing', action: logEvents.API_CALL, payload: { url, error } });
            errorFallbackHandler({
                Message: 'Network error getPing',
                Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
            }, this.state.lang, this.props.location.pathname, this.props.location.search, this.props.navigate);
        }
    }

    async getAppInstances() {
        const deviceId = this.state.value && this.state.value.device && this.state.value.device.id ? this.state.value.device.id : '';

        const url = API.endpoint.appInstances.replace('{deviceId}', deviceId);

        try {
            const apiResponse = await new Promise((resolve, reject) => {
                API.get(url, (response, error) => {
                    if (error) {
                        reject(error);
                    } else {
                        resolve(response);
                    }
                });
            });

            logger(logLevels.DEBUG, { source: 'DataProvider.getAppInstances', action: logEvents.API_CALL, payload: { url, apiResponse } });

            // Extract bundle_id from each instance
            const appInstancesIds = apiResponse.data.map((data) => data.attributes.bundle_id);

            // Extract id from each instance relationships
            const appRelationshipsIds = apiResponse.data.map((data) => data.relationships.app.data.id);

            // create an array of unique bundle_ids from appInstancesIds and appRelationshipsIds and exclude the empty ones
            const appInstances = [...new Set([...appInstancesIds, ...appRelationshipsIds])].filter((bundleId) => bundleId);

            const newState = {
                appInstances,
                timestamp: new Date().getTime(),
            };
            localStorage.setItem(localStorageKeys.SPOTLIGHT_CACHE_APP_INSTANCES, JSON.stringify(newState));
            const currentState = this.state;
            currentState.value.appInstances = appInstances;

            this.setState(currentState);
        } catch (error) {
            logger(logLevels.DEBUG, { source: 'DataProvider.getAppInstances', action: logEvents.API_CALL, payload: { url, error } });
            errorFallbackHandler({
                Message: 'Network error getAppInstances',
                Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
            }, this.state.lang, this.props.location.pathname, this.props.location.search, this.props.navigate);
        }

        return this.state.value.appInstances;
    }

    async getApps() {
        // Fetch bundleIds from app instance
        const bundleIds = await this.getAppInstances();

        if (!bundleIds || bundleIds.length === 0) {
            // Save empty {} to the localstorage SPOTLIGHT_CACHE_APP_LICENSES
            const newState = {
                appLicenses: {},
                timestamp: new Date().getTime(),
            };
            localStorage.setItem(localStorageKeys.SPOTLIGHT_CACHE_APP_LICENSES, JSON.stringify(newState));
            const currentState = this.state;
            currentState.value.appLicenses = {};
            this.setState(currentState);
            return;
        }

        try {
            const appLicenses = {};

            // Use Promise.all to fetch all apps concurrently
            const promises = bundleIds.map(async(bundleId) => {
                const url = API.endpoint.apps.replace('{bundleId}', bundleId);
                const apiResponse = await new Promise((resolve, reject) => {
                    API.get(url, (response, error) => {
                        if (error) {
                            reject(error);
                        } else {
                            resolve(response);
                        }
                    });
                });

                logger(logLevels.DEBUG, { source: 'DataProvider.getApps', action: logEvents.API_CALL, payload: { url, apiResponse } });

                const { id } = apiResponse.data;
                const { license_type } = apiResponse.data.attributes;
                appLicenses[id] = { license_type };
            });

            // Wait for all promises to resolve
            await Promise.all(promises);

            // made editional call to check password manager license since it is not included in the app instances
            const passwManagerLicenseUrl = API.endpoint.passwManLicense;
            const passwManagerLicense = await new Promise((resolve, reject) => {
                API.get(passwManagerLicenseUrl, (response, error) => {
                    if (error) {
                        reject(error);
                    } else {
                        resolve(response);
                    }
                });
            });

            // exstract the license type from the if it exists
            const passwManagerLicenseType = passwManagerLicense.data[0] ? passwManagerLicense.data[0].attributes.type : null;

            // add the password manager license to the appLicenses if it exists
            passwManagerLicenseType ? appLicenses.pasm0 = { license_type: passwManagerLicenseType } : null;

            // Save appLicenses to the state and localstorage SPOTLIGHT_CACHE_APP_LICENSES
            const newState = {
                appLicenses,
                timestamp: new Date().getTime(),
            };
            localStorage.setItem(localStorageKeys.SPOTLIGHT_CACHE_APP_LICENSES, JSON.stringify(newState));
            const currentState = this.state;
            currentState.value.appLicenses = appLicenses;

            this.setState(currentState);
        } catch (error) {
            logger(logLevels.DEBUG, { source: 'DataProvider.getApps', action: logEvents.API_CALL, payload: { error } });
            errorFallbackHandler({
                Message: 'Network error getApps',
                Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
            }, this.state.lang, this.props.location.pathname, this.props.location.search, this.props.navigate);
        }
    }

    /**
     * Read the cache
     * Check the ttl and if is older than the configured ttl get data again from backend
     */
    getCachedState() {
        let cacheFound = null;

        try {
            const cachedData = {
                ...JSON.parse(localStorage.getItem(localStorageKeys.SPOTLIGHT_CACHE_APP_PRICES)),
                ...JSON.parse(localStorage.getItem(localStorageKeys.SPOTLIGHT_CACHE_FEATURES)),
                ...JSON.parse(localStorage.getItem(localStorageKeys.SPOTLIGHT_CACHE_DEVICE_DATA)),
                ...JSON.parse(localStorage.getItem(localStorageKeys.SPOTLIGHT_CACHE_PAYMENT_URLS) || '{"paymentUrls": {}}'),
                ...JSON.parse(localStorage.getItem(localStorageKeys.SPOTLIGHT_CACHE_PROMOTIONS) || '{"promotions": {}}'),
                ...JSON.parse(localStorage.getItem(localStorageKeys.SPOTLIGHT_CACHE_LICENSES)),
                ...JSON.parse(localStorage.getItem(localStorageKeys.SPOTLIGHT_CACHE_APP_RECOMMENDATIONS)),
                ...JSON.parse(localStorage.getItem(localStorageKeys.SPOTLIGHT_CACHE_PAYMENT_TYPES)),
                ...JSON.parse(localStorage.getItem(localStorageKeys.SPOTLIGHT_CACHE_TRANSACTIONS)),
                ...JSON.parse(localStorage.getItem(localStorageKeys.SPOTLIGHT_CACHE_LICENSE_INTEGRATIONS)),
                ...JSON.parse(localStorage.getItem(localStorageKeys.SPOTLIGHT_CACHE_PING)),
                ...JSON.parse(localStorage.getItem(localStorageKeys.SPOTLIGHT_CACHE_APP_INSTANCES)),
                ...JSON.parse(localStorage.getItem(localStorageKeys.SPOTLIGHT_CACHE_APP_LICENSES)),
            };

            logger(logLevels.DEBUG, { source: 'DataProvider', action: logEvents.LOAD_CACHE, payload: { cachedData } });

            // Add sessionStorage item for cache read
            if ((cachedData.prices && Object.keys(cachedData.prices).length !== 0) &&
                (cachedData.features && Object.keys(cachedData.features).length !== 0) &&
                (cachedData.paymentUrls && Object.keys(cachedData.paymentUrls).length !== 0)) {
                sessionStorage.setItem('SPOTLIGHT_CACHE_DATA', true);
            } else sessionStorage.setItem('SPOTLIGHT_CACHE_DATA', false);

            // Remove device data from cache to trigger another my-device call (only for ExternalCall)
            if (cachedData.device && Object.keys(cachedData.device).length !== 0) {
                delete cachedData.device;
            }

            const ttl = new Date().getTime() - cachedData.timestamp;

            if (ttl > config.backendCacheTtl) {
                logger(logLevels.DEBUG, { source: 'DataProvider', action: logEvents.CLEAR_CACHE, payload: { status: 'Cache has expired' } });
                sessionStorage.setItem('SPOTLIGHT_CACHE_DATA', false);
                return null;
            }
            cacheFound = cachedData;
        } catch (err) {
            logger(logLevels.WARN, { source: 'DataProvider', action: logEvents.LOAD_CACHE, payload: { err } });
        }

        return cacheFound;
    }

    render() {
        const { loading } = this.state;
        const { allDataFetched, fallbackCondition, errorCode } = this.validateData();

        // render only if all the data is set in state
        if (
            allDataFetched &&
            !loading
        ) {
            return (
                <DataContext.Provider {...this.props} value={this.state.value}>
                    <Outlet />
                </DataContext.Provider>
            );
        }

        if (!loading && fallbackCondition) {
            return (
                <div className="showElementDelay">
                    <Fallback
                        {...this.props}
                        errorCode={errorCode}
                        errorPath={this.props.location.pathname}
                        ping={ping}
                    />
                </div>
            );
        }

        return null;
    }

    async fetchAllData() {
        try {
            const results = await Promise.allSettled([
                this.fetchPaymentUrls(),
                this.fetchPrices(),
                // this.fetchFeatures(),
                this.fetchDeviceData(),
                this.fetchAppLicenses(),
                this.fetchPromotions(),
                this.fetchLicenses(),
                this.fetchPing(),
            ]);

            // Log the results of each promise
            results.forEach((result, index) => {
                if (result.status === 'rejected') {
                    logger(logLevels.ERROR, {
                        source: 'DataProvider.fetchAllData',
                        action: logEvents.ERROR,
                        payload: {
                            Message: `Error in fetchAllData - Promise ${index + 1}`,
                            Exception: (result.reason instanceof Error) ? result.reason.toString() : `${JSON.stringify(result.reason)}`,
                        },
                    });
                }
            });

            const { allDataFetched } = this.validateData();

            if (allDataFetched) {
                this.setState({ loading: false });
            }
        } catch (error) {
            logger(logLevels.ERROR, {
                source: 'DataProvider.fetchAllData',
                action: logEvents.ERROR,
                payload: {
                    Message: 'Error in fetchAllData',
                    Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
                },
            });

            this.setState({ loading: false });
        }
    }

    // Helper function: Fetch payment URLs
    async fetchPaymentUrls() {
        try {
            const { paymentUrls } = this.state.value;

            if (!paymentUrls.yearly && this.users === '1') {
                await this.getPaymentUrls('yearly', 1);
            }

            if (!paymentUrls.yearly5Devices && this.users === '5') {
                await this.getPaymentUrls('yearly', 5);
            }

            if (!paymentUrls.yearly10Devices && (this.users === '10' || this.isStore)) {
                await this.getPaymentUrls('yearly', 10);
            }

            if (!paymentUrls.twoYears && this.users === '1') {
                await this.getPaymentUrls('twoYears', 1);
            }

            if (!paymentUrls.twoYears5Devices && this.users === '5') {
                await this.getPaymentUrls('twoYears', 5);
            }

            if (!paymentUrls.twoYears10Devices && (this.users === '10' || this.isStore)) {
                await this.getPaymentUrls('twoYears', 10);
            }

            if (this.isPaidUpsell && !paymentUrls.paidUpsell) {
                this.getLicenses(this.licenseId);
            }
        } catch (error) {
            logger(logLevels.ERROR, {
                source: 'DataProvider.fetchPaymentUrls',
                action: logEvents.ERROR,
                payload: {
                    Message: 'Error in fetchPaymentUrls',
                    Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
                },
            });
        }
    }

    async fetchPrices() {
        try {
            const { prices } = this.state.value;

            if (!prices) {
                if (this.users === '10') {
                    await Promise.all([
                        this.getPrices(12, 10),
                        this.getPrices(24, 10),
                    ]);
                } else if (this.users === '5') {
                    await Promise.all([
                        this.getPrices(12, 5),
                        this.getPrices(24, 5),
                    ]);
                } else if (this.users === '1' && !this.isStore) {
                    await Promise.all([
                        this.getPrices(12, 1),
                        this.getPrices(24, 1),
                    ]);
                } else {
                    await Promise.all([
                        this.getPrices(12, 1),
                        this.getPrices(24, 1),
                        this.getPrices(12, 5),
                        this.getPrices(24, 5),
                        this.getPrices(12, 10),
                        this.getPrices(24, 10),
                    ]);
                }
            } else {
                // Prices exist, but check if we need to fetch for specific user count
                if (this.users === '10' && (!prices.yearly10Devices || Object.keys(prices.yearly10Devices).length === 0)) {
                    await Promise.all([
                        this.getPrices(12, 10),
                        this.getPrices(24, 10),
                    ]);
                } else if (this.users === '5' && (!prices.yearly5Devices || Object.keys(prices.yearly5Devices).length === 0)) {
                    await Promise.all([
                        this.getPrices(12, 5),
                        this.getPrices(24, 5),
                    ]);
                } else if (this.users === '1' && (!prices.yearly || Object.keys(prices.yearly).length === 0)) {
                    await Promise.all([
                        this.getPrices(12, 1),
                        this.getPrices(24, 1),
                    ]);
                }
            }
            if (this.isStore && prices && prices.yearly10Devices && Object.keys(prices.yearly10Devices).length === 0) {
                await Promise.all([
                    this.getPrices(12, 10),
                    this.getPrices(24, 10),
                ]);
            }
        } catch (error) {
            logger(logLevels.ERROR, {
                source: 'DataProvider.fetchPrices',
                action: logEvents.ERROR,
                payload: {
                    Message: 'Error in fetchPrices',
                    Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
                },
            });
        }
    }

    // async fetchFeatures() {
    //     try {
    //         if (!this.state.value.features) {
    //             await this.getAppIntegrations();
    //         }
    //     } catch (error) {
    //         logger(logLevels.ERROR, {
    //             source: 'DataProvider.fetchFeatures',
    //             action: logEvents.ERROR,
    //             payload: {
    //                 Message: 'Error in fetchFeatures',
    //                 Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
    //             },
    //         });
    //     }
    // }
    async fetchDeviceData() {
        // to not call getDeviceData if it is localhost
        if (window.location.hostname === 'localhost' || window.location.hostname === '') {
            return;
        }

        try {
            if (!this.state.value.device) {
                await this.getDeviceData();
            }
        } catch (error) {
            logger(logLevels.ERROR, {
                source: 'DataProvider.fetchDeviceData',
                action: logEvents.ERROR,
                payload: {
                    Message: 'Error in fetchDeviceData',
                    Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
                },
            });
        }
    }

    async fetchAppLicenses() {
        try {
            if (!this.state.value.appLicenses) {
                await this.getApps();
            }
        } catch (error) {
            logger(logLevels.ERROR, {
                source: 'DataProvider.fetchAppLicenses',
                action: logEvents.ERROR,
                payload: {
                    Message: 'Error in fetchAppLicenses',
                    Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
                },
            });
        }
    }

    async fetchPromotions() {
        try {
            const { promotions } = this.state.value;

            if (!promotions.trial && this.users === '1') {
                await this.getPromotions('trial', 1);
            } else if (!promotions.trial5Devices && this.users === '5') {
                await this.getPromotions('trial', 5);
            } else if (!promotions.trial10Devices && this.users === '10') {
                await this.getPromotions('trial', 10);
            } else if (!promotions.trial && !this.users) {
                await Promise.all([
                    this.getPromotions('trial', 1),
                    this.getPromotions('trial', 5),
                    this.getPromotions('trial', 10),
                ]);
            } else {
                // Promotions.trial exists, but check if we need specific user count data or bundleId data within trialPromotion
                if (this.users === '10' && (!promotions.trial10Devices || (promotions.trial10Devices && promotions.trial10Devices[config.trialPromotion] && !promotions.trial10Devices[config.trialPromotion][this.promotionBundleId]))) {
                    await this.getPromotions('trial', 10, this.promotionBundleId);
                } else if (this.users === '5' && (!promotions.trial5Devices || (promotions.trial5Devices && promotions.trial5Devices[config.trialPromotion] && !promotions.trial5Devices[config.trialPromotion][this.promotionBundleId]))) {
                    await this.getPromotions('trial', 5, this.promotionBundleId);
                } else if (this.users === '1' && (!promotions.trial || (promotions.trial && promotions.trial[config.trialPromotion] && !promotions.trial[config.trialPromotion][this.promotionBundleId]))) {
                    await this.getPromotions('trial', 1, this.promotionBundleId);
                }
            }

            // fetch promotions for ui store, if its missing
            if (this.isUiStoreSale && (!promotions.yearly || (promotions.yearly && !promotions.yearly[config.uiStorePromoCode[1]]))) {
                const uiStoreSaleYearlyPromises = [
                    this.getPromotions('yearly', 1, 'aps10'),
                    this.getPromotions('yearly', 1, 'adwp1'),
                ];

                await Promise.all(uiStoreSaleYearlyPromises);
            }

            if (this.isUiStoreSale && (!promotions.twoYears || (promotions.twoYears && !promotions.twoYears[config.uiStorePromoCode[1]]))) {
                const uiStoreSaleTwoYearsPromises = [
                    this.getPromotions('twoYears', 1, 'aps10'),
                    this.getPromotions('twoYears', 1, 'adwp1'),
                ];

                await Promise.all(uiStoreSaleTwoYearsPromises);
            }

            if (this.isUiStoreSale) {
                const uiStorePromoCode = config.uiStorePromoCode && config.uiStorePromoCode[1];

                const uiStoreSaleYearly10DevicesPromises = [];
                // Fetch ui store yearly 10 devices promotions for carousel Bundles (excluding adwp1 - only 1 user)
                config.carousel_config.appIds.forEach(bundleId => {
                    if (bundleId === 'adwp1') {
                        return;
                    }

                    const hasYearlyPromotionForBundle = promotions.yearly10Devices &&
                        uiStorePromoCode &&
                        promotions.yearly10Devices[uiStorePromoCode] &&
                        promotions.yearly10Devices[uiStorePromoCode][bundleId];

                    if (!hasYearlyPromotionForBundle) {
                        uiStoreSaleYearly10DevicesPromises.push(this.getPromotions('yearly', 10, bundleId));
                    }
                });

                if (uiStoreSaleYearly10DevicesPromises.length) {
                    await Promise.all(uiStoreSaleYearly10DevicesPromises);
                }

                const uiStoreSaleTwoYears10DevicesPromises = [];
                // Fetch ui store two years 10 devices promotions for carousel Bundles (excluding adwp1 - only 1 user)
                config.carousel_config.appIds.forEach(bundleId => {
                    if (bundleId === 'adwp1') {
                        return;
                    }

                    const hasTwoYearsPromotionForBundle = promotions.twoYears10Devices &&
                        uiStorePromoCode &&
                        promotions.twoYears10Devices[uiStorePromoCode] &&
                        promotions.twoYears10Devices[uiStorePromoCode][bundleId];

                    if (!hasTwoYearsPromotionForBundle) {
                        uiStoreSaleTwoYears10DevicesPromises.push(this.getPromotions('twoYears', 10, bundleId));
                    }
                });

                if (uiStoreSaleTwoYears10DevicesPromises.length) {
                    await Promise.all(uiStoreSaleTwoYears10DevicesPromises);
                }
            }

            if (!promotions.yearly && this.users === '1') {
                await this.getPromotions('yearly', 1);
            } else if (!promotions.yearly5Devices && this.users === '5') {
                await this.getPromotions('yearly', 5);
            } else if (!promotions.yearly10Devices && this.users === '10') {
                await this.getPromotions('yearly', 10);
            } else if (!promotions.yearly && !this.users) {
                await Promise.all([
                    this.getPromotions('yearly', 1),
                    this.getPromotions('yearly', 5),
                    this.getPromotions('yearly', 10),
                ]);
            } else {
                // Promotions.yearly exists, but check if we need specific user count data or bundleId data
                const yearlyPromoCode = config.upgradePromotions?.high;
                if (this.users === '10' && (!promotions.yearly10Devices || (this.upgradePromotions && yearlyPromoCode && promotions.yearly10Devices[yearlyPromoCode] && !promotions.yearly10Devices[yearlyPromoCode][this.bundleId]))) {
                    await this.getPromotions('yearly', 10, this.bundleId);
                } else if (this.users === '5' && (!promotions.yearly5Devices || (this.upgradePromotions && yearlyPromoCode && promotions.yearly5Devices[yearlyPromoCode] && !promotions.yearly5Devices[yearlyPromoCode][this.bundleId]))) {
                    await this.getPromotions('yearly', 5, this.bundleId);
                } else if (this.users === '1' && (!promotions.yearly || (this.upgradePromotions && yearlyPromoCode && promotions.yearly[yearlyPromoCode] && !promotions.yearly[yearlyPromoCode][this.bundleId]))) {
                    await this.getPromotions('yearly', 1, this.bundleId);
                }
            }

            if (!promotions.twoYears && this.users === '1' && this.upgradePromotions) {
                await this.getPromotions('twoYears', 1);
            } else if (!promotions.twoYears5Devices && this.users === '5' && this.upgradePromotions) {
                await this.getPromotions('twoYears', 5);
            } else if (!promotions.twoYears10Devices && this.users === '10' && this.upgradePromotions) {
                await this.getPromotions('twoYears', 10);
            } else if (!promotions.twoYears && !this.users) {
                await Promise.all([
                    this.getPromotions('twoYears', 1),
                    this.getPromotions('twoYears', 5),
                    this.getPromotions('twoYears', 10),
                ]);
            } else if (this.upgradePromotions) {
                // TwoYears promotions exist, but check if we need specific user count data or bundleId data
                const yearlyPromoCode = config.upgradePromotions?.high;
                if (this.users === '10' && (!promotions.twoYears10Devices || (yearlyPromoCode && promotions.twoYears10Devices[yearlyPromoCode] && !promotions.twoYears10Devices[yearlyPromoCode][this.bundleId]))) {
                    await this.getPromotions('twoYears', 10, this.bundleId);
                } else if (this.users === '5' && (!promotions.twoYears5Devices || (yearlyPromoCode && promotions.twoYears5Devices[yearlyPromoCode] && !promotions.twoYears5Devices[yearlyPromoCode][this.bundleId]))) {
                    await this.getPromotions('twoYears', 5, this.bundleId);
                } else if (this.users === '1' && (!promotions.twoYears || (yearlyPromoCode && promotions.twoYears[yearlyPromoCode] && !promotions.twoYears[yearlyPromoCode][this.bundleId]))) {
                    await this.getPromotions('twoYears', 1, this.bundleId);
                }
            }

            // Get additional promotions based on plans_offer parameter
            if ((!promotions.yearly && this.offer.isValid && this.offer.is30Discount) ||
                (promotions.yearly && !promotions.yearly[config.plans30DiscountPromotion] && this.offer.isValid && this.offer.is30Discount)) {
                await Promise.all([
                    this.getPromotions('yearly', 'aapd5'),
                    this.getPromotions('yearly', 'isec0'),
                    this.getPromotions('yearly', 'ispm0'),
                ]);
            }

            if ((!promotions.yearly && this.offer.isValid && this.offer.isCascadingDiscount) || (promotions.yearly && this.offer.isValid && this.offer.isCascadingDiscount)) {
                await Promise.all([
                    this.getPromotions('yearly', 'isec0'),
                    this.getPromotions('yearly', 'aps20'),
                ]);
            }

            if ((!promotions.yearly && this.isUpsellIdentityAssistant) ||
                (promotions.yearly && this.isUpsellIdentityAssistant && !promotions.yearly[config.upsell_identity_assistant_config.promotionCode])) {
                await this.getPromotions('yearly', config.upsell_identity_assistant_config.appId);
            }
        } catch (error) {
            logger(logLevels.ERROR, {
                source: 'DataProvider.fetchPromotions',
                action: logEvents.ERROR,
                payload: {
                    Message: 'Error in fetchPromotions',
                    Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
                },
            });
        }
    }

    async fetchLicenses() {
        try {
            if (!this.state.value.licenses && this.licenseId) {
                await this.getLicenses(this.licenseId);
            }
        } catch (error) {
            logger(logLevels.ERROR, {
                source: 'DataProvider.fetchLicenses',
                action: logEvents.ERROR,
                payload: {
                    Message: 'Error in fetchLicenses',
                    Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
                },
            });
        }
    }
    async fetchPing() {
        try {
            if (!this.state.value.ping) {
                await this.getPing();
            }
        } catch (error) {
            logger(logLevels.ERROR, {
                source: 'DataProvider.fetchPing',
                action: logEvents.ERROR,
                payload: {
                    Message: 'Error in fetchPing',
                    Exception: (error instanceof Error) ? error.toString() : `${JSON.stringify(error)}`,
                },
            });
        }
    }

    validateData() {
        // Verify that all required data is present
        const prices = deepFind(this.state, 'value.prices');
        const paymentUrls = deepFind(this.state, 'value.paymentUrls');
        const licenses = this.licenseId ? deepFind(this.state, 'value.licenses') : true;
        const licenseIntegrations = (this.licenseId && (licenses && (licenses.isLicenseUpgrade || licenses.isLicenseOnboarding))) ? deepFind(this.state, 'value.licenseIntegrations') : true;
        const ping = deepFind(this.state, 'value.ping');
        const appLicenses = deepFind(this.state, 'value.appLicenses');
        const promotions = deepFind(this.state, 'value.promotions');

        const paymentUrlsPromotionsLength = (this.licenseId && (licenses && licenses.additionalCalls)) ?
            paymentUrls && paymentUrls.promotions && Object.keys(paymentUrls.promotions).length === config.promotionsBundles.length : true;

        const paymentUrlsRenewLicenseLength = (this.licenseId && (licenses && licenses.renewLicense)) ?
            paymentUrls && paymentUrls.renewLicense && Object.keys(paymentUrls.renewLicense).length === config.promotionsBundles.length : true;

        const paymentUrlsUpsellLicenseLength = (this.licenseId && (licenses && licenses.renewLicense && licenses.upsellTo)) ?
            paymentUrls && paymentUrls.upsellLicense && Object.keys(paymentUrls.upsellLicense).length === config.promotionsBundles.length : true;

        const paymentUrlsRetrieverLength = (this.licenseId && (licenses && licenses.retrieverRedirect)) ?
            paymentUrls && paymentUrls.retriever && Object.keys(paymentUrls.retriever).length === config.promotionsBundles.length : true;

        const pricesCurrentPlan = (this.licenseId && (licenses && (licenses.additionalCalls || (licenses.renewLicense && licenses.customLicense) || licenses.paidUpsell))) ?
            prices && prices.currentPlan && Object.keys(prices.currentPlan).length === config.promotionsBundles.length : true;

        const paymentUrlsPaidUpsellLength = (this.licenseId && (licenses && licenses.paidUpsell)) ?
            paymentUrls && paymentUrls.paidUpsell && Object.keys(paymentUrls.paidUpsell).length === config.promotionsBundles.length : true;

        const paymentUrlsPaidUpsellSecondaryLength = (this.licenseId && (licenses && licenses.paidUpsell && (licenses.upsellTo.secondaryPromotion || licenses.upsellTo.onlyMerge))) ?
            paymentUrls && paymentUrls.paidUpsellSecondary && Object.keys(paymentUrls.paidUpsellSecondary).length === config.promotionsBundles.length : true;

        // Determine if all data is fetched
        const allDataFetched = !!prices && !!paymentUrls && !!licenses && !!ping && !!appLicenses && !!promotions && licenseIntegrations && paymentUrlsPromotionsLength && pricesCurrentPlan && paymentUrlsPaidUpsellLength && paymentUrlsRenewLicenseLength && paymentUrlsUpsellLicenseLength && paymentUrlsRetrieverLength && paymentUrlsPaidUpsellSecondaryLength;

        // Determine if fallback should be rendered
        const fallbackCondition = !prices || !licenses || !paymentUrlsPromotionsLength || !pricesCurrentPlan;

        const errorCode = `${prices ? '1' : '0'}${licenses ? '1' : '0'}${paymentUrlsPromotionsLength ? '1' : '0'}${pricesCurrentPlan ? '1' : '0'}${appLicenses ? '1' : '0'}`;

        return {allDataFetched, fallbackCondition, errorCode};
    }

    // to clear the SPOTLIGHT_CACHE_APP_LICENSES cache and remove appLicenses from the state
    clearAppLicensesCache() {
        localStorage.removeItem(localStorageKeys.SPOTLIGHT_CACHE_APP_LICENSES);
        const currentState = this.state;
        delete currentState.value.appLicenses;
        this.setState(currentState);
    }
}

DataProviderClass.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
    location: PropTypes.object,
    access_token: PropTypes.string,
    navigate: PropTypes.func,
    params: PropTypes.object,
};

const DataProvider = routerHelper(DataProviderClass);

export { DataProvider, DataContext };
